"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { QuizCTA as QuizLink } from "~/components/features/quiz";
import Container from "~/componentsDS/Layout/Container";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Reveal from "~/componentsDS/Reveal";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { TextRow } from "./TextRow";
import { OrbitJar } from "./OrbitJar";

const TREATMENT_ROWS = [
    {
        items: ["Hot flashes", "Heat", "Night sweats", "Cold flushes", "Heart palpitations", "Hot flashes"],
        highlight: "Heat",
    },
    {
        items: ["Irritability", "Mood swings", "Mood", "Anxiety", "Low mood", "Overwhelm"],
        highlight: "Mood",
    },
    {
        items: ["Waking at 3am", "Trouble sleeping", "Restlessness", "Sleep", "Fatigue"],
        highlight: "Sleep",
    },
    {
        items: ["Brain fog", "Forgetfulness", "Trouble focusing", "Mind", "Low motivation"],
        highlight: "Mind",
    },
    {
        items: ["Dry skin", "Skin & hair", "Thinning hair", "Brittle nails", "Breakouts", "Dullness"],
        highlight: "Skin & hair",
    },
    {
        items: ["Irregular cycles", "Heavy periods", "Cycle", "Cramping", "Bloating", "PMS"],
        highlight: "Cycle",
    },
    {
        items: ["Low libido", "Joint aches", "Tender breasts", "Weight changes", "Body"],
        highlight: "Body",
    },
] as const;

export default function MeetOestraSection() {
    const sectionRef = useRef<HTMLElement>(null);
    const reduceMotion = Boolean(useReducedMotion());
    const isInView = useInView(sectionRef, { amount: 0.15 });
    const shouldAnimate = isInView && !reduceMotion;

    return (
        <section ref={sectionRef} className="overflow-hidden pt-16 lg:pt-24">
            <Container>
                <Reveal>
                    <Heading as="h2" size="md" sizes={{ lg: "lg" }} className="text-center text-pureWhite mb-1">
                        Meet Oestra<sup className="text-[0.35em] top-[-1.5em]">®</sup>
                    </Heading>
                </Reveal>

                <Paragraph size="lg" className="text-center text-pureWhite">
                    Whole-body hormone support for <br className="lg:hidden"/>
                    feeling more like yourself again.
                </Paragraph>

                <div className="flex flex-col">
                    <div className="relative order-1 mx-auto -mb-4 -mt-12 aspect-[298/303] w-full max-w-[350px] md:order-2 md:left-1/2 md:mx-0 md:mb-0 md:mt-6 md:h-[420px] md:w-screen md:max-w-none md:-translate-x-1/2 md:aspect-auto lg:mt-[60px] lg:h-[460px] pointer-events-none">
                        <div
                            aria-label="Symptoms supported by Oestra: Hot flashes, heat, night sweats, cold flushes, heart palpitations, irritability, mood swings, anxiety, low mood, overwhelm, sleep problems, fatigue, brain fog, forgetfulness, trouble focusing, low motivation, dry skin, thinning hair, brittle nails, breakouts, dullness, irregular cycles, heavy periods, cramping, bloating, PMS, low libido, joint aches, tender breasts, and weight changes."
                            className="absolute left-1/2 top-[62px] z-0 hidden -translate-x-1/2 flex-col items-center gap-3 font-suisseBP text-[11px] leading-none md:flex sm:top-[42px] sm:gap-5 sm:text-[13px] lg:top-0 lg:gap-6 lg:text-[16px]"
                        >
                            {TREATMENT_ROWS.map((row, index) => (
                                <TextRow
                                    key={row.highlight}
                                    items={row.items}
                                    highlight={row.highlight}
                                    index={index}
                                    reduceMotion={!shouldAnimate}
                                />
                            ))}
                        </div>

                        <div className="relative h-full md:absolute md:left-1/2 md:top-[-40px] md:z-10 md:h-auto md:w-[410px] md:-translate-x-1/2 lg:top-[-104px] lg:w-[500px]">
                            <motion.div
                                className="relative h-full"
                                animate={shouldAnimate ? {
                                    y: [0, -10, 0],
                                    rotate: [-0.35, 0.35, -0.35],
                                } : undefined}
                                transition={{
                                    duration: 9,
                                    ease: "easeInOut",
                                    repeat: Infinity,
                                }}
                            >
                                <OrbitJar sizes="(min-width: 1024px) 620px, (min-width: 768px) 410px, 350px" />
                            </motion.div>
                        </div>
                    </div>

                    <div className="order-2 mt-8 flex justify-center md:order-1">
                        <QuizLink
                            href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                            variant="white"
                            size="md"
                            eventData={{
                                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                                widget: "meet_product",
                                meta_data: {
                                    click_text: "Take the OESTRA® assessment",
                                    product_name: "oestra",
                                },
                            }}
                            className="max-sm:w-full"
                        >
                            <TextWithRegisteredMark text="Take the OESTRA® assessment" />
                        </QuizLink>
                    </div>
                </div>
            </Container>
        </section>
    );
}
